import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../services/axiosInstance";

export const fetchSubAccounts = createAsyncThunk("subAccounts/fetchAll", async (_, thunkAPI) => {
  try {
    const res = await axiosInstance.get("/SubAccounts");
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
  }
});

export const createSubAccount = createAsyncThunk("subAccounts/create", async (payload, thunkAPI) => {
  try {
    const res = await axiosInstance.post("/SubAccounts", payload);
    const isPending = res.data.isPending ?? true;
    return {
      ...payload, id: res.data.id, extension: res.data.extension,
      isActive: !isPending, isPending,
      password: undefined, confirmPassword: undefined,
    };
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
  }
});

export const updateSubAccount = createAsyncThunk("subAccounts/update", async ({ id, data }, thunkAPI) => {
  try {
    await axiosInstance.put(`/SubAccounts/${id}`, data);
    return { id, data };
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
  }
});

export const toggleSubAccount = createAsyncThunk("subAccounts/toggle", async (id, thunkAPI) => {
  try {
    const res = await axiosInstance.patch(`/SubAccounts/${id}/toggle`);
    return res.data;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.message);
  }
});

export const deleteSubAccount = createAsyncThunk("subAccounts/delete", async (id, thunkAPI) => {
  try {
    await axiosInstance.delete(`/SubAccounts/${id}`);
    return id;
  } catch (err) {
    // Pass 409 payload through so the UI can show the sole-agent warning
    if (err.response?.status === 409) {
      return thunkAPI.rejectWithValue({ ...err.response.data, _409: true });
    }
    return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
  }
});

export const deleteSubAccountForced = createAsyncThunk("subAccounts/deleteForced", async (id, thunkAPI) => {
  try {
    await axiosInstance.delete(`/SubAccounts/${id}?force=true`);
    return id;
  } catch (err) {
    return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
  }
});

const subAccountsSlice = createSlice({
  name: "subAccounts",
  initialState: { list: [], status: "idle", error: null },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubAccounts.pending,   (s) => { s.status = "loading"; })
      .addCase(fetchSubAccounts.fulfilled, (s, a) => { s.status = "success"; s.list = a.payload; })
      .addCase(fetchSubAccounts.rejected,  (s, a) => { s.status = "error"; s.error = a.payload; })

      .addCase(createSubAccount.fulfilled, (s, a) => { s.list.push(a.payload); })

      .addCase(updateSubAccount.fulfilled, (s, a) => {
        const idx = s.list.findIndex((r) => r.id === a.payload.id);
        if (idx !== -1) s.list[idx] = { ...s.list[idx], ...a.payload.data };
      })

      .addCase(toggleSubAccount.fulfilled, (s, a) => {
        const idx = s.list.findIndex((r) => r.id === a.payload.id);
        if (idx !== -1) s.list[idx].isActive = a.payload.isActive;
      })

      .addCase(deleteSubAccount.fulfilled, (s, a) => {
        s.list = s.list.filter((r) => r.id !== a.payload);
      })

      .addCase(deleteSubAccountForced.fulfilled, (s, a) => {
        s.list = s.list.filter((r) => r.id !== a.payload);
      });
  },
});

export default subAccountsSlice.reducer;
